import User, { getLatestLicences, Role } from "./User";
import { UserCapabilityOption } from "./UserCapabilityOption";
import { doesAdminAlwaysHaveCapability, UserCapability } from "./UserCapability";
import { RoleSettingsModule } from "@/store/modules/RoleSettingsModule";
import {
  formatUserRole,
  formatUserRoleDescription,
} from "@/utils/LocalisationHelper";
import Vue from "vue";
import { adminRightsAccess } from "@/permissions/UserPermissions";
import type { RoleSettings } from "./RoleSettingsDefaults";
import { createDefaultRoleSettings } from "./RoleSettingsDefaults";

// Types and factory defaults live in `RoleSettingsDefaults.ts`, a store-free leaf module, to break
// the import cycle documented there. Re-exported here so existing importers keep working unchanged.
//
// `export type` for the two types is required, not stylistic: a plain `export { ... } from` makes
// the transpiler emit a runtime re-export, and since an interface and a type alias have no runtime
// value, webpack warns "export 'RoleSettings' was not found in './RoleSettingsDefaults'".
export type {
  RoleToConfigurationMap,
  RoleSettings,
} from "./RoleSettingsDefaults";
export {
  getRoleConfigurationForRole,
  getCapabilitiesThatRequireBasicLicence,
  createDefaultRoleSettings,
} from "./RoleSettingsDefaults";


export function getCapabilityOptionForRole(
  role: Role,
  capability: UserCapability,
): UserCapabilityOption {
  const currentConfiguration = RoleSettingsModule.roleSettings;
  const defaultConfiguration = createDefaultRoleSettings();

  return (
    currentConfiguration.roleToConfiguration[role].capabilityMap[capability] ?
    currentConfiguration.roleToConfiguration[role].capabilityMap[capability] :
    defaultConfiguration.roleToConfiguration[role].capabilityMap[capability]
  );
}

export function getRoleId(role: Role) {
  switch (role) {
    case Role.ADMIN:
      return 1;
    case Role.OFFICE:
      return 2;
    case Role.PROJECT_MANAGER:
      return 3;
    case Role.TECHNICIAN_AND_LEAK_DETECTION:
      return 4;
    case Role.LEAK_DETECTION:
      return 5;
    case Role.TECHNICIAN_1:
      return 6;
    case Role.TECHNICIAN_2:
      return 7;
    case Role.CUSTOM_ROLE_1:
      return 8;
    case Role.CUSTOM_ROLE_2:
      return 9;
    case Role.CUSTOM_ROLE_3:
      return 10;
    case Role.CUSTOM_ROLE_4:
      return 11;
    case Role.CUSTOM_ROLE_5:
      return 12;
  }
}

export function getRoleLabelForRoleSettings(
  roleSettings: RoleSettings,
  role: Role,
  vue: Vue
) {
  return (
    roleSettings.roleToConfiguration[role]?.label ?? formatUserRole(vue, role)
  );
}

export function getRoleLabel(role: Role, vue: Vue) {
  return getRoleLabelForRoleSettings(
    RoleSettingsModule.roleSettings,
    role,
    vue
  );
}

export function getRoleActive(role: Role, vue: Vue) {
  const roleSettings = RoleSettingsModule.roleSettings;
  return (
    roleSettings.roleToConfiguration[role]?.roleActive ??
    formatUserRole(vue, role)
  );
}

export function getRoleDescription(role: Role, vue: Vue) {
  const roleSettings = RoleSettingsModule.roleSettings;
  return (
    roleSettings.roleToConfiguration[role]?.description ??
    formatUserRoleDescription(vue, role)
  );
}

/**
 * Check user capability & permissions
 * @param capabilities
 * @param user
 */
function havePermission(capabilities: UserCapability[], user: User) {
  let havePermission = false;
  const role = user.role;

  if (!role) {
    return false;
  }

  havePermission = capabilities.some((capability) => {
    if (
      getCapabilityOptionForRole(role, capability) == UserCapabilityOption.YES ||
      getCapabilityOptionForRole(role, capability) ==
        UserCapabilityOption.CONDITIONAL
    ) {
      return true;
    }
    else if (
      adminRightsAccess(user) && 
      doesAdminAlwaysHaveCapability(capability)
    ) {
      if (
        capability === UserCapability.SEE_ALL_PROJECTS &&
        getLatestLicences(user.licenceInfo)?.length === 0
      ) {
        return false;
      }

      return true;
    }
  });
  
  return havePermission;
}

export function doesUserHaveCapability(
  user: User | null,
  ...capabilities: UserCapability[]
) {

  if (user?.role == null) {
    return false;
  }

  return havePermission(capabilities, user);
}